import { Badge, Card, ErrorBox, Link, Note, Page, Select, Stat, Table, Td } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fmtDateTime, fmtLei, fmtNum, fmtShare, plural } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { rpc } from '../lib/supabase.ts';
import type { SalesTeam, TeamMember, TerritoryStats } from '../lib/types.ts';
import { Refreshing, SkeletonCards, SkeletonTable } from './loading.tsx';
import { INACTIVITY, NotLinked, PERIODS } from './Sales.tsx';

// Zona directorului / managerului de vanzari (docs/26): agentii pe teritoriul lor (judetele
// alocate), judetele cu si fara agent, concurenta si activitatea echipei. Prin RLS, pe codurile
// din abonament; permisiunea sales.view.

export function CountyLink({ county }: { county: string }) {
  if (county === 'Necunoscut') return <>{county}</>;
  return (
    <Link to={`/app/achizitii?county=${encodeURIComponent(county)}`} className="hover:underline">
      {county}
    </Link>
  );
}

function Competitor({ t }: { t: TerritoryStats }) {
  const c = t.top_competitor;
  if (!c) return <span className="text-gri-700">—</span>;
  return (
    <>
      {c.supplier_id ? (
        <Link to={`/app/furnizori/${c.supplier_id}`} className="hover:underline">
          {c.name ?? `Furnizorul ${c.supplier_id}`}
        </Link>
      ) : (
        (c.name ?? '—')
      )}
      <div className="text-xs text-gri-700">
        {fmtLei(c.value_ron)} · {fmtShare(c.value_ron, t.market_value)} din piață
      </div>
    </>
  );
}

/** O cifra mica dintr-un card de agent. */
function Metric({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <div>
      <div className="eticheta text-gri-700">{label}</div>
      <div className="text-lg font-extrabold tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-gri-700">{sub}</div> : null}
    </div>
  );
}

function lastSeen(m: TeamMember | undefined): string {
  if (!m?.last_sign_in_at) return 'n-a intrat încă';
  return `ultima intrare ${fmtDateTime(m.last_sign_in_at)}`;
}

function AgentCard({
  a,
  member,
  inactiveLabel,
}: {
  a: SalesTeam['agents'][number];
  member: TeamMember | undefined;
  inactiveLabel: string;
}) {
  return (
    <Card title={member?.name ?? 'Agent'} actions={<span className="text-xs">{member?.role}</span>}>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-1.5">
          {a.counties.length ? (
            a.counties.map((c) => (
              <span key={c} className="bg-gri-200 px-1.5 py-0.5 text-xs">
                <CountyLink county={c} />
              </span>
            ))
          ) : (
            <Badge tone="warn">fără județe</Badge>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric
            label="Vânzări"
            value={fmtLei(a.ours_value)}
            sub={plural(a.ours_acquisitions, 'achiziție', 'achiziții')}
          />
          <Metric
            label="Cota de piață"
            value={fmtShare(a.ours_value, a.market_value)}
            sub={`din ${fmtLei(a.market_value)}`}
          />
          <Metric
            label="Clienți"
            value={fmtNum(a.our_clients)}
            sub={a.new_clients === null ? undefined : `${fmtNum(a.new_clients)} noi`}
          />
          <Metric label={`Inactivi (${inactiveLabel})`} value={fmtNum(a.inactive_clients)} />
          <Metric label="Oportunități" value={fmtNum(a.prospects)} sub="cumpără de la alții" />
          <div>
            <div className="eticheta text-gri-700">Concurent principal</div>
            <div className="text-sm">
              <Competitor t={a} />
            </div>
          </div>
        </div>
        <div className="border-t border-gri-300 pt-2 text-xs text-gri-700">
          {lastSeen(member)} · {plural(member?.alerts ?? 0, 'alertă activă', 'alerte active')} ·{' '}
          {plural(member?.exports_30d ?? 0, 'export', 'exporturi')} în 30 de zile
        </div>
      </div>
    </Card>
  );
}

export function ManagementPage() {
  const { org } = useOrg();
  const [period, setPeriod] = useState<number | null>(3);
  const [inactive, setInactive] = useState(3);
  const q = useQuery({
    queryKey: ['sales_team', org.id, period, inactive],
    queryFn: () =>
      rpc<SalesTeam>('sales_team', {
        p_org: org.id,
        p_months: period,
        p_inactive_months: inactive,
      }),
  });
  const t = q.data;
  const members = new Map((t?.members ?? []).map((m) => [m.user_id, m]));
  const inactiveLabel = INACTIVITY.find((x) => x.months === inactive)?.label ?? `${inactive} luni`;
  const uncovered = (t?.counties ?? []).filter(
    (c) => !c.agents.length && Number(c.market_value) > 0 && c.county !== 'Necunoscut',
  );
  return (
    <Page
      title="Management vânzări"
      actions={
        <>
          <div className="w-40">
            <Select
              aria-label="Perioada"
              value={period ?? ''}
              onChange={(e) => setPeriod(e.target.value ? Number(e.target.value) : null)}
            >
              {PERIODS.map((p) => (
                <option key={p.label} value={p.months ?? ''}>
                  {p.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-44">
            <Select
              aria-label="Clienți inactivi de"
              value={inactive}
              onChange={(e) => setInactive(Number(e.target.value))}
            >
              {INACTIVITY.map((x) => (
                <option key={x.months} value={x.months}>
                  Inactivi de {x.label}
                </option>
              ))}
            </Select>
          </div>
        </>
      }
    >
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <>
          <SkeletonCards count={4} />
          <SkeletonTable rows={6} cols={5} />
        </>
      ) : null}
      {t ? (
        <Refreshing active={q.isFetching && !q.isLoading}>
          <div className="space-y-4">
            <p className="-mt-2 text-xs text-gri-700">
              Piața = achizițiile directe pe codurile CPV din abonament. Fiecare agent răspunde de
              județele lui (SEAP nu spune cine a făcut vânzarea).
            </p>
            {!t.supplier ? <NotLinked /> : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Piața"
                value={fmtLei(t.totals.market_value)}
                sub={`${plural(t.totals.market_acquisitions, 'achiziție', 'achiziții')} · ${plural(t.totals.market_authorities, 'instituție', 'instituții')}`}
              />
              <Stat
                label="Vânzările voastre"
                value={fmtLei(t.totals.ours_value)}
                sub={plural(t.totals.ours_acquisitions, 'achiziție', 'achiziții')}
                accent
              />
              <Stat
                label="Cota de piață"
                value={fmtShare(t.totals.ours_value, t.totals.market_value)}
                sub={
                  t.totals.top_competitor
                    ? `liderul: ${t.totals.top_competitor.name ?? '—'}, ${fmtShare(t.totals.top_competitor.value_ron, t.totals.market_value)}`
                    : undefined
                }
              />
              <Stat
                label="Oportunități"
                value={fmtNum(t.totals.prospects)}
                sub="instituții care cumpără pe codurile voastre, dar nu de la voi"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat
                label="Clienți"
                value={fmtNum(t.totals.our_clients)}
                sub={
                  t.totals.new_clients === null
                    ? 'în perioada aleasă'
                    : `${fmtNum(t.totals.new_clients)} noi în perioada aleasă`
                }
              />
              <Stat
                label={`Inactivi de ${inactiveLabel}`}
                value={fmtNum(t.totals.inactive_clients)}
                sub={
                  <Link to="/app/vanzari" className="underline">
                    lista, în Vânzări
                  </Link>
                }
              />
              <Stat
                label="Județe fără agent"
                value={fmtNum(uncovered.length)}
                sub={
                  uncovered.length
                    ? `cu piață: ${uncovered
                        .slice(0, 4)
                        .map((c) => c.county)
                        .join(', ')}${uncovered.length > 4 ? '…' : ''}`
                    : 'toată piața are agent'
                }
              />
            </div>

            <h2 className="eticheta pt-2 text-cerneala">Agenții</h2>
            {t.agents.length ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {t.agents.map((a) => (
                  <AgentCard
                    key={a.user_id}
                    a={a}
                    member={members.get(a.user_id)}
                    inactiveLabel={inactiveLabel}
                  />
                ))}
              </div>
            ) : (
              <Note>
                Firma nu are agenți cu județe. Un membru devine agent când are județe și un rol fără
                „toate județele”: îl setezi din{' '}
                <Link to="/app/echipa" className="font-medium underline">
                  Echipă
                </Link>
                .
              </Note>
            )}

            <Card title="Județele">
              <Table
                head={['Județul', 'Piața', 'Voi', 'Clienți', 'Oportunități', 'Concurent principal']}
                empty={!t.counties.length}
              >
                {t.counties.map((c) => (
                  <tr
                    key={c.county}
                    className={!c.agents.length && Number(c.market_value) > 0 ? 'bg-semnal-50' : ''}
                  >
                    <Td>
                      <span className="font-medium">
                        <CountyLink county={c.county} />
                      </span>
                      <div className="text-xs text-gri-700">
                        {c.agents.length ? (
                          c.agents.map((id) => members.get(id)?.name ?? '—').join(', ')
                        ) : (
                          <Badge tone="warn">fără agent</Badge>
                        )}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">
                      {fmtLei(c.market_value)}
                      <div className="text-xs text-gri-700">
                        {plural(c.market_acquisitions, 'achiziție', 'achiziții')}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">
                      {fmtLei(c.ours_value)}
                      <div className="text-xs text-gri-700">
                        {fmtShare(c.ours_value, c.market_value)} din piață
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">
                      {fmtNum(c.our_clients)}
                      <div className="text-xs text-gri-700">
                        {c.new_clients === null ? '' : `${fmtNum(c.new_clients)} noi · `}
                        {fmtNum(c.inactive_clients)} inactivi
                      </div>
                    </Td>
                    <Td className="tabular-nums">{fmtNum(c.prospects)}</Td>
                    <Td className="max-w-xs">
                      <Competitor t={c} />
                    </Td>
                  </tr>
                ))}
              </Table>
            </Card>

            <Card title="Concurența pe codurile voastre">
              <Table
                head={['Furnizorul', 'Valoare', 'Din piață', 'Achiziții', 'Instituții', 'Județe']}
                empty={!t.competitors.length}
              >
                {t.competitors.map((c, i) => (
                  <tr key={c.supplier_id ?? `t${i}`}>
                    <Td className="max-w-md">
                      {c.supplier_id ? (
                        <Link
                          to={`/app/furnizori/${c.supplier_id}`}
                          className="font-medium hover:underline"
                        >
                          {c.name ?? `Furnizorul ${c.supplier_id}`}
                        </Link>
                      ) : (
                        (c.name ?? '—')
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-right tabular-nums">
                      {fmtLei(c.value_ron)}
                    </Td>
                    <Td className="tabular-nums">{fmtShare(c.value_ron, t.totals.market_value)}</Td>
                    <Td className="tabular-nums">{fmtNum(c.acquisitions)}</Td>
                    <Td className="tabular-nums">{fmtNum(c.authorities)}</Td>
                    <Td className="tabular-nums">{fmtNum(c.counties)}</Td>
                  </tr>
                ))}
              </Table>
            </Card>

            <Card title="Activitatea echipei în Elsa">
              <Table
                head={[
                  'Membrul',
                  'Rolul',
                  'Teritoriul',
                  'Ultima intrare',
                  'Alerte',
                  'Exporturi (30 de zile)',
                ]}
                empty={!t.members.length}
              >
                {t.members.map((m) => (
                  <tr key={m.user_id}>
                    <Td className="max-w-xs">
                      <div className="font-medium">{m.name}</div>
                      {m.name !== m.email ? (
                        <div className="text-xs text-gri-700">{m.email}</div>
                      ) : null}
                    </Td>
                    <Td>{m.role}</Td>
                    <Td className="max-w-xs text-xs">
                      {m.all_counties ? 'toate județele' : m.counties.join(', ') || '—'}
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums">
                      {m.last_sign_in_at ? fmtDateTime(m.last_sign_in_at) : '—'}
                    </Td>
                    <Td className="tabular-nums">{fmtNum(m.alerts)}</Td>
                    <Td className="tabular-nums">{fmtNum(m.exports_30d)}</Td>
                  </tr>
                ))}
              </Table>
            </Card>
          </div>
        </Refreshing>
      ) : null}
    </Page>
  );
}
